import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Calculator, ChevronRight, Info, CheckCircle } from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt$(n) {
  if (n == null || isNaN(n)) return '$0.00';
  return '$' + parseFloat(n).toFixed(2);
}

function fmtPct(n) {
  return (n * 100).toFixed(n % 0.01 === 0 ? 0 : 1) + '%';
}

const DAY_TYPES = [
  { id: 'weekday',       label: 'Weekday',       color: '#3b82f6' },
  { id: 'saturday',      label: 'Saturday',      color: '#f59e0b' },
  { id: 'sunday',        label: 'Sunday',        color: '#ef4444' },
  { id: 'publicHoliday', label: 'Public Holiday', color: '#8b5cf6' },
];

const SHIFT_TYPES = [
  { id: 'standard',      label: 'Standard',           description: 'Normal daytime hours' },
  { id: 'afternoon',     label: 'Afternoon (+12.5%)',  description: 'Finishing after 6pm ≤ midnight' },
  { id: 'night',         label: 'Night (+15%)',        description: 'Finishing after midnight ≤ 8am' },
  { id: 'permanentNight',label: 'Perm. Night (+30%)',  description: 'Solely/predominantly night shift' },
];

// ── Sub-components ────────────────────────────────────────────────────────────

function Card({ children, style }) {
  return (
    <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 10, padding: '20px 24px', ...style }}>
      {children}
    </div>
  );
}

function SectionTitle({ children, sub }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--hp-text)' }}>{children}</div>
      {sub && <div style={{ color: 'var(--hp-dim)', fontSize: 12, marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function Pill({ label, color }) {
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 99, background: color + '22', color, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em' }}>
      {label}
    </span>
  );
}

// ── Rate Calculator Widget ────────────────────────────────────────────────────

function RateCalculator({ grades, facilityLevels, loadings }) {
  const allGrades = [...(grades || []), ...(facilityLevels || [])];
  const [selectedGrade, setSelectedGrade] = useState('grade1');
  const [casual, setCasual] = useState(true);
  const [dayType, setDayType] = useState('weekday');
  const [shiftType, setShiftType] = useState('permanentNight');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculate = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ action: 'calculate', grade: selectedGrade, casual, dayType, shiftType });
      const res = await fetch(`/.netlify/functions/fair-work-rates?${params}`);
      const data = await res.json();
      setResult(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [selectedGrade, casual, dayType, shiftType]);

  useEffect(() => { calculate(); }, [calculate]);

  return (
    <Card>
      <SectionTitle sub="Select classification and conditions — rate calculates instantly">
        Rate Calculator
      </SectionTitle>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 16, marginBottom: 20 }}>
        {/* Grade */}
        <div>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--hp-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Classification</label>
          <select value={selectedGrade} onChange={e => setSelectedGrade(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: 14, fontFamily: 'var(--font-body)' }}>
            <optgroup label="Transport Workers">
              {(grades || []).map(g => <option key={g.id} value={g.id}>{g.label} — ${g.base}/hr</option>)}
            </optgroup>
            <optgroup label="Distribution Facility">
              {(facilityLevels || []).map(g => <option key={g.id} value={g.id}>{g.label} — ${g.base}/hr</option>)}
            </optgroup>
          </select>
        </div>

        {/* Employment type */}
        <div>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--hp-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Employment Type</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[['casual', 'Casual'], ['fulltime', 'Full-time']].map(([val, lbl]) => (
              <button key={val} onClick={() => setCasual(val === 'casual')}
                style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: `2px solid ${(val === 'casual') === casual ? '#3b82f6' : 'var(--hp-border)'}`, background: (val === 'casual') === casual ? 'rgba(59,130,246,0.08)' : 'var(--hp-bg)', color: (val === 'casual') === casual ? '#3b82f6' : 'var(--hp-dim)', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                {lbl}
              </button>
            ))}
          </div>
        </div>

        {/* Day type */}
        <div>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--hp-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Day Type</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {DAY_TYPES.map(d => (
              <button key={d.id} onClick={() => setDayType(d.id)}
                style={{ padding: '6px 12px', borderRadius: 8, border: `2px solid ${dayType === d.id ? d.color : 'var(--hp-border)'}`, background: dayType === d.id ? d.color + '18' : 'var(--hp-bg)', color: dayType === d.id ? d.color : 'var(--hp-dim)', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                {d.label}
              </button>
            ))}
          </div>
        </div>

        {/* Shift type */}
        <div>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--hp-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Shift Type</label>
          <select value={shiftType} onChange={e => setShiftType(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: 14, fontFamily: 'var(--font-body)' }}>
            {SHIFT_TYPES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          {dayType !== 'weekday' && (
            <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 4 }}>
              Shift penalty not applied — {dayType} penalty rate takes precedence
            </div>
          )}
        </div>
      </div>

      {/* Result */}
      {result && !result.error && (
        <div style={{ background: 'var(--hp-bg)', border: '2px solid var(--hp-border)', borderRadius: 10, padding: '16px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--hp-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Calculated Rate</div>
              <div style={{ fontSize: 36, fontWeight: 900, color: '#22c55e', fontVariantNumeric: 'tabular-nums' }}>{fmt$(result.rate)}<span style={{ fontSize: 16, color: 'var(--hp-dim)', fontWeight: 500 }}>/hr</span></div>
              <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <Pill label={result.labels?.casual} color="#3b82f6" />
                <Pill label={result.labels?.day} color="#ef4444" />
                {result.labels?.shift !== '—' && result.labels?.shift !== 'Standard' && <Pill label={result.labels?.shift} color="#8b5cf6" />}
              </div>
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.8 }}>
              <div style={{ color: 'var(--hp-dim)', marginBottom: 4, fontWeight: 700 }}>Rate breakdown</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '2px 16px' }}>
                <span style={{ color: 'var(--hp-dim)' }}>Base rate ({result.grade})</span>
                <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt$(result.breakdown?.base)}</span>
                {result.breakdown?.casualLoading > 0 && <>
                  <span style={{ color: 'var(--hp-dim)' }}>+ Casual loading (25%)</span>
                  <span style={{ textAlign: 'right', color: '#3b82f6', fontVariantNumeric: 'tabular-nums' }}>+{fmt$(result.breakdown.casualLoading)}</span>
                </>}
                {result.breakdown?.dayPenalty > 0 && <>
                  <span style={{ color: 'var(--hp-dim)' }}>+ {result.labels?.day}</span>
                  <span style={{ textAlign: 'right', color: '#ef4444', fontVariantNumeric: 'tabular-nums' }}>+{fmt$(result.breakdown.dayPenalty)}</span>
                </>}
                {result.breakdown?.shiftPenalty > 0 && <>
                  <span style={{ color: 'var(--hp-dim)' }}>+ {result.labels?.shift}</span>
                  <span style={{ textAlign: 'right', color: '#8b5cf6', fontVariantNumeric: 'tabular-nums' }}>+{fmt$(result.breakdown.shiftPenalty)}</span>
                </>}
                <span style={{ borderTop: '1px solid var(--hp-border)', paddingTop: 4, fontWeight: 700 }}>Total</span>
                <span style={{ borderTop: '1px solid var(--hp-border)', paddingTop: 4, textAlign: 'right', fontWeight: 700, color: '#22c55e', fontVariantNumeric: 'tabular-nums' }}>{fmt$(result.breakdown?.total)}</span>
              </div>
            </div>
          </div>
          <div style={{ marginTop: 12, fontSize: 11, color: 'var(--hp-dim)' }}>
            Source: {result.award?.name} [{result.award?.code}] · Effective {result.award?.effectiveDate}
          </div>
        </div>
      )}
    </Card>
  );
}

// ── Night Sort Quick View ─────────────────────────────────────────────────────

function NightSortRates({ nightSort2025, nightSort2026, upcoming, effectiveDate }) {
  const [copied, setCopied]       = useState(null); // '2025' | '2026'
  const [showYear, setShowYear]   = useState('2026'); // default to upcoming rates

  const ns    = showYear === '2026' ? nightSort2026 : nightSort2025;
  const sorter = ns?.find(g => g.id === 'grade1');
  const tl     = ns?.find(g => g.id === 'grade6');

  // Previous year for comparison
  const sorter25 = nightSort2025?.find(g => g.id === 'grade1');
  const tl25     = nightSort2025?.find(g => g.id === 'grade6');

  function applyToNightSort(year) {
    const s = year === '2026' ? nightSort2026?.find(g => g.id === 'grade1') : nightSort2025?.find(g => g.id === 'grade1');
    const t = year === '2026' ? nightSort2026?.find(g => g.id === 'grade6') : nightSort2025?.find(g => g.id === 'grade6');
    if (!s || !t) return;
    localStorage.setItem('hp-nightsort-inputs', JSON.stringify({
      sorterSunday:  s.sundayCasual,
      sorterWeekday: s.weekdayCasual,
      tlSunday:      t.sundayCasual,
      tlWeekday:     t.weekdayCasual,
      superRate: 0.12, wcRate: 0.032, markupPct: 0.15,
      sundayBreakPaid: true, weekdayBreakPaid: true,
      sundayWeeks: 52, weekdayWeeks: 208,
    }));
    setCopied(year);
    setTimeout(() => setCopied(null), 3000);
  }

  if (!sorter || !tl) return null;

  const daysUntil = upcoming?.daysUntil;

  return (
    <Card style={{ borderLeft: `4px solid ${showYear === '2026' ? '#f59e0b' : '#3b82f6'}` }}>
      {/* Upcoming rate alert */}
      {upcoming?.increaseAnnounced && (
        <div style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, color: '#ef4444', fontSize: 14, marginBottom: 4 }}>
              ⚠️ Rate increase in {daysUntil} days — 1 July 2026
            </div>
            <div style={{ fontSize: 13, color: 'var(--hp-dim)', lineHeight: 1.6 }}>
              FWC Annual Wage Review 2026 decision ({upcoming.increaseAnnounced.decisionDate}):
              <strong style={{ color: 'var(--hp-text)' }}> +{upcoming.increaseAnnounced.pct}% on all modern award rates.</strong>{' '}
              Your night sort contract must reflect these rates from 1 July 2026.
              If your contract starts before July 1, include an escalation clause.
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <SectionTitle sub={`RDTA MA000038 · Grade 1 Casual (Sorters) + Grade 6 Casual (Team Leader)`}>
            Night Sort — Verified Rates
          </SectionTitle>
          <div style={{ display: 'flex', gap: 6 }}>
            {['2025', '2026'].map(y => (
              <button key={y} onClick={() => setShowYear(y)}
                style={{ padding: '4px 14px', borderRadius: 6, border: `2px solid ${showYear === y ? (y === '2026' ? '#f59e0b' : '#3b82f6') : 'var(--hp-border)'}`, background: showYear === y ? (y === '2026' ? 'rgba(245,158,11,0.1)' : 'rgba(59,130,246,0.1)') : 'var(--hp-bg)', color: showYear === y ? (y === '2026' ? '#f59e0b' : '#3b82f6') : 'var(--hp-dim)', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                {y === '2026' ? '★ From 1 Jul 2026' : 'Current 2025–26'}
              </button>
            ))}
          </div>
        </div>
        <button onClick={() => applyToNightSort(showYear)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, background: copied === showYear ? '#22c55e' : (showYear === '2026' ? '#f59e0b' : '#3b82f6'), color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, fontFamily: 'var(--font-body)' }}>
          {copied === showYear ? <><CheckCircle size={14} /> Applied!</> : <><ChevronRight size={14} /> Apply {showYear} rates to Night Sort</>}
        </button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--hp-border)' }}>
              <th style={{ textAlign: 'left', padding: '8px 0', color: 'var(--hp-dim)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>Role</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', color: 'var(--hp-dim)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>Grade</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', color: 'var(--hp-dim)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>Base</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', color: '#ef4444', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>Sunday Casual</th>
              <th style={{ textAlign: 'right', padding: '8px 0', color: '#3b82f6', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>Weekday Night</th>
              {showYear === '2026' && <th style={{ textAlign: 'right', padding: '8px 0 8px 12px', color: '#22c55e', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>vs 2025</th>}
            </tr>
          </thead>
          <tbody>
            {[
              { role: 'Sorter / Runner', g: sorter, g25: sorter25, note: '9 positions' },
              { role: 'Team Leader',     g: tl,     g25: tl25,     note: '1 position — supervises 10–15' },
            ].map(({ role, g, g25, note }) => (
              <tr key={role} style={{ borderTop: '1px solid var(--hp-border)' }}>
                <td style={{ padding: '12px 0' }}>
                  <div style={{ fontWeight: 700 }}>{role}</div>
                  <div style={{ fontSize: 11, color: 'var(--hp-dim)' }}>{note}</div>
                </td>
                <td style={{ padding: '12px', textAlign: 'right', color: 'var(--hp-dim)' }}>{g.label}</td>
                <td style={{ padding: '12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt$(g.base)}</td>
                <td style={{ padding: '12px', textAlign: 'right', fontWeight: 700, color: '#ef4444', fontVariantNumeric: 'tabular-nums' }}>{fmt$(g.sundayCasual)}</td>
                <td style={{ padding: showYear === '2026' ? '12px' : '12px 0', textAlign: 'right', fontWeight: 700, color: '#3b82f6', fontVariantNumeric: 'tabular-nums' }}>{fmt$(g.weekdayCasual)}</td>
                {showYear === '2026' && g25 && (
                  <td style={{ padding: '12px 0 12px 12px', textAlign: 'right', fontSize: 12, color: '#22c55e', fontVariantNumeric: 'tabular-nums' }}>
                    +{fmt$(g.sundayCasual - g25.sundayCasual)} Sun<br/>
                    +{fmt$(g.weekdayCasual - g25.weekdayCasual)} WD
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 8, fontSize: 12, color: 'var(--hp-dim)', lineHeight: 1.6 }}>
        <strong>Formula: </strong>Rate = Base × (1 + Casual 25% + Penalty%) ·
        Sunday = +100% (200% total) · Permanent night shift = +30% · Penalties do NOT stack
      </div>
    </Card>
  );
}

// ── Full Rate Table ───────────────────────────────────────────────────────────

function RateTable({ grades, effectiveDate }) {
  const conditions = [
    { label: 'FT Standard',        casual: false, dayType: 'weekday',       shiftType: 'standard',       color: '#6b7280' },
    { label: 'FT Perm. Night',     casual: false, dayType: 'weekday',       shiftType: 'permanentNight', color: '#8b5cf6' },
    { label: 'FT Sunday',          casual: false, dayType: 'sunday',        shiftType: 'standard',       color: '#ef4444' },
    { label: 'Casual Standard',    casual: true,  dayType: 'weekday',       shiftType: 'standard',       color: '#3b82f6' },
    { label: 'Casual Perm. Night', casual: true,  dayType: 'weekday',       shiftType: 'permanentNight', color: '#06b6d4' },
    { label: 'Casual Saturday',    casual: true,  dayType: 'saturday',      shiftType: 'standard',       color: '#f59e0b' },
    { label: 'Casual Sunday',      casual: true,  dayType: 'sunday',        shiftType: 'standard',       color: '#ef4444' },
    { label: 'Casual Pub. Hol.',   casual: true,  dayType: 'publicHoliday', shiftType: 'standard',       color: '#8b5cf6' },
  ];

  function calcRate(base, { casual, dayType, shiftType }) {
    const L = {
      casual: 0.25, afternoonShift: 0.125, nightShift: 0.15,
      permanentNightShift: 0.30, saturday: 0.50, sunday: 1.00, publicHoliday: 1.50,
    };
    let penalty = 0;
    if (dayType === 'sunday') penalty = L.sunday;
    else if (dayType === 'saturday') penalty = L.saturday;
    else if (dayType === 'publicHoliday') penalty = L.publicHoliday;
    else if (shiftType === 'permanentNight') penalty = L.permanentNightShift;
    else if (shiftType === 'night') penalty = L.nightShift;
    else if (shiftType === 'afternoon') penalty = L.afternoonShift;
    return +(base * (1 + (casual ? L.casual : 0) + penalty)).toFixed(2);
  }

  return (
    <Card>
      <SectionTitle sub={`All grades × all conditions · MA000038 · Effective ${effectiveDate}`}>
        Full Rate Table
      </SectionTitle>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, whiteSpace: 'nowrap' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--hp-border)' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px 8px 0', color: 'var(--hp-dim)', fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>Grade</th>
              <th style={{ textAlign: 'right', padding: '8px 8px', color: 'var(--hp-dim)', fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>Base</th>
              {conditions.map(c => (
                <th key={c.label} style={{ textAlign: 'right', padding: '8px 8px', color: c.color, fontWeight: 700, fontSize: 11 }}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(grades || []).map((g, i) => (
              <tr key={g.id} style={{ borderTop: '1px solid var(--hp-border)', background: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)' }}>
                <td style={{ padding: '8px 12px 8px 0', fontWeight: 600 }}>
                  {g.label}
                  <div style={{ fontSize: 10, color: 'var(--hp-dim)', fontWeight: 400, maxWidth: 160, whiteSpace: 'normal' }}>{g.description}</div>
                </td>
                <td style={{ padding: '8px', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt$(g.base)}</td>
                {conditions.map(c => (
                  <td key={c.label} style={{ padding: '8px', textAlign: 'right', color: c.color, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                    {fmt$(calcRate(g.base, c))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ── Date Rate Checker ─────────────────────────────────────────────────────────

const RATE_PERIODS = [
  {
    from: '2025-07-01', to: '2026-06-30',
    label: '2025–26', confirmed: true,
    increase: null,
    grades: {
      grade1: 25.65, grade2: 26.27, grade3: 26.57, grade4: 27.04, grade5: 27.37,
      grade6: 27.68, grade7: 28.09, grade8: 28.90, grade9: 29.39, grade10: 30.12,
    },
    source: 'https://library.fairwork.gov.au/award/?krn=MA000038',
    sourceLabel: 'Fair Work — RDTA MA000038',
  },
  {
    from: '2026-07-01', to: '2027-06-30',
    label: '2026–27', confirmed: true,
    increase: { pct: 4.75, decisionDate: '2026-06-02', decisionUrl: 'https://www.fwc.gov.au/hearings-decisions/major-cases/annual-wage-reviews/annual-wage-review-2026' },
    grades: {
      grade1: 26.87, grade2: 27.51, grade3: 27.83, grade4: 28.32, grade5: 28.67,
      grade6: 29.00, grade7: 29.42, grade8: 30.27, grade9: 30.77, grade10: 31.55,
    },
    source: 'https://www.fwc.gov.au/hearings-decisions/major-cases/annual-wage-reviews/annual-wage-review-2026',
    sourceLabel: 'FWC Annual Wage Review 2026 decision',
  },
];

function calcNightSortForBase(base) {
  return {
    sunday:  +(base * 2.25).toFixed(2),
    weekday: +(base * 1.55).toFixed(2),
  };
}

function DateRateChecker() {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  const d = new Date(date + 'T12:00:00');
  const period = RATE_PERIODS.find(p => d >= new Date(p.from) && d <= new Date(p.to + 'T23:59:59'));
  const isFuture = d > new Date('2027-06-30');
  const isPast   = d < new Date('2025-07-01');

  const sorter1 = period ? calcNightSortForBase(period.grades.grade1) : null;
  const tl6     = period ? calcNightSortForBase(period.grades.grade6)  : null;

  return (
    <Card>
      <SectionTitle sub="Pick any date — see which award year and rates apply">
        Rate on Date
      </SectionTitle>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--hp-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Select date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: 15, fontFamily: 'var(--font-body)', fontWeight: 600 }} />
        </div>
        {/* Quick jump buttons */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignSelf: 'flex-end', paddingBottom: 2 }}>
          {[
            { label: 'Today', val: new Date().toISOString().split('T')[0] },
            { label: '1 Jul 2026', val: '2026-07-01' },
            { label: '1 Jul 2027', val: '2027-07-01' },
          ].map(b => (
            <button key={b.label} onClick={() => setDate(b.val)}
              style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--hp-border)', background: date === b.val ? 'var(--hp-gold)' : 'var(--hp-surface)', color: date === b.val ? '#fff' : 'var(--hp-dim)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
              {b.label}
            </button>
          ))}
        </div>
      </div>

      {isPast && (
        <div style={{ padding: '16px', background: 'rgba(107,114,128,0.08)', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-dim)', fontSize: 13 }}>
          Rates before 1 July 2025 are not held in this engine. Check the Fair Work pay guide archive.
        </div>
      )}

      {isFuture && (
        <div style={{ padding: '16px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, fontSize: 13, lineHeight: 1.6 }}>
          <strong style={{ color: '#f59e0b' }}>Rates not yet confirmed</strong> for periods beyond 30 June 2027.
          The FWC typically announces the next increase in May/June each year.
          Check back closer to 1 July 2027.
        </div>
      )}

      {period && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <span style={{ padding: '4px 14px', borderRadius: 99, background: 'rgba(34,197,94,0.12)', color: '#22c55e', fontWeight: 800, fontSize: 13 }}>
              {period.label} Award Year
            </span>
            <span style={{ fontSize: 13, color: 'var(--hp-dim)' }}>
              {period.from} to {period.to}
            </span>
            {period.increase && (
              <span style={{ fontSize: 12, color: '#f59e0b', fontWeight: 600 }}>
                +{period.increase.pct}% from prior year
              </span>
            )}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--hp-border)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 0', color: 'var(--hp-dim)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>Role</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', color: 'var(--hp-dim)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>Base</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', color: '#ef4444', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>Sunday Casual</th>
                  <th style={{ textAlign: 'right', padding: '8px 0', color: '#3b82f6', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>Weekday Night Casual</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderTop: '1px solid var(--hp-border)' }}>
                  <td style={{ padding: '10px 0' }}><strong>Sorter / Runner</strong> <span style={{ fontSize: 11, color: 'var(--hp-dim)' }}>Grade 1</span></td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt$(period.grades.grade1)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#ef4444', fontVariantNumeric: 'tabular-nums' }}>{fmt$(sorter1.sunday)}</td>
                  <td style={{ padding: '10px 0', textAlign: 'right', fontWeight: 700, color: '#3b82f6', fontVariantNumeric: 'tabular-nums' }}>{fmt$(sorter1.weekday)}</td>
                </tr>
                <tr style={{ borderTop: '1px solid var(--hp-border)' }}>
                  <td style={{ padding: '10px 0' }}><strong>Team Leader</strong> <span style={{ fontSize: 11, color: 'var(--hp-dim)' }}>Grade 6</span></td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt$(period.grades.grade6)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#ef4444', fontVariantNumeric: 'tabular-nums' }}>{fmt$(tl6.sunday)}</td>
                  <td style={{ padding: '10px 0', textAlign: 'right', fontWeight: 700, color: '#3b82f6', fontVariantNumeric: 'tabular-nums' }}>{fmt$(tl6.weekday)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--hp-dim)' }}>
            Source:{' '}
            <a href={period.source} target="_blank" rel="noopener noreferrer"
              style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}>
              {period.sourceLabel}
            </a>
            {period.increase && (<>
              {' · '}FWC decision:{' '}
              <a href={period.increase.decisionUrl} target="_blank" rel="noopener noreferrer"
                style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}>
                Annual Wage Review 2026 ({period.increase.decisionDate})
              </a>
            </>)}
          </div>
        </div>
      )}
    </Card>
  );
}

// ── Sources Card ──────────────────────────────────────────────────────────────

const SOURCES = [
  {
    label: 'Road Transport Distribution Award 2020 — Full Text',
    url: 'https://library.fairwork.gov.au/award/?krn=MA000038',
    desc: 'MA000038 · Fair Work Commission · Complete award including all clauses, rates and conditions',
    tag: 'Primary Source',
    tagColor: '#22c55e',
  },
  {
    label: 'Annual Wage Review 2026 — FWC Decision',
    url: 'https://www.fwc.gov.au/hearings-decisions/major-cases/annual-wage-reviews/annual-wage-review-2026',
    desc: 'Handed down 2 June 2026 · +4.75% to all modern award rates effective 1 July 2026',
    tag: 'Wage Review',
    tagColor: '#ef4444',
  },
  {
    label: 'Minimum Wages — Fair Work Ombudsman',
    url: 'https://www.fairwork.gov.au/about-us/workplace-laws/annual-wage-review/annual-wage-review-2026',
    desc: 'FWO summary of the 2026 annual wage review and new minimum wage rates',
    tag: 'FWO',
    tagColor: '#3b82f6',
  },
  {
    label: 'Pay and Conditions Tool (PACT) — Fair Work',
    url: 'https://calculate.fairwork.gov.au/payguides/fairwork/ma000038/docx',
    desc: 'Official PACT calculator — cross-check any rate calculation against this tool',
    tag: 'Verify',
    tagColor: '#8b5cf6',
  },
  {
    label: 'RDTA Pay Guide (Excel download)',
    url: 'https://www.fairwork.gov.au/pay-and-wages/minimum-wages/award-and-agreement-free-wages/pay-guides',
    desc: 'Fair Work Ombudsman pay guides — download the Road Transport Distribution Award guide',
    tag: 'Pay Guide',
    tagColor: '#f59e0b',
  },
  {
    label: 'Ministers\' Media Release — +4.75% Award Increase',
    url: 'https://ministers.dewr.gov.au/rishworth/award-and-minimum-wage-workers-receive-least-475-pay-increase',
    desc: 'Government media release confirming the 4.75% modern award increase from 1 July 2026',
    tag: 'Media Release',
    tagColor: '#6b7280',
  },
];

function SourcesCard() {
  return (
    <Card>
      <SectionTitle sub="All rates and calculations in this engine are sourced from Fair Work Commission and Fair Work Ombudsman official publications">
        Sources & References
      </SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: 10 }}>
        {SOURCES.map(s => (
          <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer"
            style={{ display: 'block', padding: '12px 14px', background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 8, textDecoration: 'none', transition: 'border-color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = s.tagColor}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--hp-border)'}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 5 }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--hp-text)', lineHeight: 1.3 }}>{s.label}</span>
              <span style={{ flexShrink: 0, padding: '2px 8px', borderRadius: 99, background: s.tagColor + '22', color: s.tagColor, fontSize: 10, fontWeight: 700, letterSpacing: '0.04em' }}>{s.tag}</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--hp-dim)', lineHeight: 1.5 }}>{s.desc}</div>
            <div style={{ marginTop: 6, fontSize: 10, color: s.tagColor, fontWeight: 600 }}>↗ {new URL(s.url).hostname}</div>
          </a>
        ))}
      </div>
    </Card>
  );
}

// ── Loadings Reference ────────────────────────────────────────────────────────

function LoadingsCard({ loadings }) {
  if (!loadings) return null;
  const items = Object.entries(loadings).map(([k, v]) => ({ key: k, ...v }));
  return (
    <Card>
      <SectionTitle sub="MA000038 — all applicable loadings and penalty rates">
        Loadings & Penalties Reference
      </SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: 10 }}>
        {items.map(item => (
          <div key={item.key} style={{ padding: '12px 14px', background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontWeight: 700, fontSize: 13 }}>{item.label}</span>
              <span style={{ fontWeight: 800, color: '#22c55e', fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>+{fmtPct(item.rate)}</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--hp-dim)', lineHeight: 1.5 }}>{item.description}</div>
            <div style={{ fontSize: 10, color: 'var(--hp-dim)', marginTop: 4, opacity: 0.7 }}>Clause {item.clause}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AwardRatesPage() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchRates = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/.netlify/functions/fair-work-rates?action=all');
      if (!res.ok) throw new Error('Rate fetch failed');
      const d = await res.json();
      setData(d);
      setLastFetched(new Date().toLocaleTimeString('en-AU'));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRates(); }, []);

  const daysUntilReview = data?.award?.nextReviewDate
    ? Math.ceil((new Date(data.award.nextReviewDate) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontWeight: 900, fontSize: 22, color: 'var(--hp-text)' }}>Award Rate Engine</h1>
          <div style={{ color: 'var(--hp-dim)', fontSize: 13, marginTop: 4 }}>
            Road Transport and Distribution Award 2020 [MA000038] · Built-in PACT calculator
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {data?.award && (
            <div style={{ fontSize: 12, color: 'var(--hp-dim)', textAlign: 'right' }}>
              <div>Effective: <strong style={{ color: 'var(--hp-text)' }}>{data.award.effectiveDate}</strong></div>
              <div>Next review: <strong style={{ color: daysUntilReview < 60 ? '#ef4444' : '#f59e0b' }}>{data.award.nextReviewDate}</strong>
                {daysUntilReview != null && <span style={{ color: 'var(--hp-dim)' }}> ({daysUntilReview} days)</span>}
              </div>
              {lastFetched && <div style={{ opacity: 0.6 }}>Last fetched: {lastFetched}</div>}
            </div>
          )}
          <button onClick={fetchRates} disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', color: 'var(--hp-text)', cursor: loading ? 'wait' : 'pointer', fontSize: 13, fontFamily: 'var(--font-body)', fontWeight: 600 }}>
            <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Award banner */}
      {data?.award && (
        <div style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <CheckCircle size={14} color="#22c55e" />
          <strong style={{ color: '#22c55e' }}>Verified:</strong>
          <span style={{ color: 'var(--hp-dim)' }}>{data.award.name} [{data.award.code}] rates loaded from Fair Work · {data.award.source}</span>
        </div>
      )}

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: 13, color: '#ef4444' }}>
          Error loading rates: {error}
        </div>
      )}

      {loading && !data && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--hp-dim)' }}>Loading award rates…</div>
      )}

      {data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <NightSortRates nightSort2025={data.nightSort2025} nightSort2026={data.nightSort2026} upcoming={data.upcoming} effectiveDate={data.award?.effectiveDate} />
          <DateRateChecker />
          <RateCalculator grades={data.grades} facilityLevels={data.facilityLevels} loadings={data.loadings} />
          <LoadingsCard loadings={data.loadings} />
          <RateTable grades={data.grades} effectiveDate={data.award?.effectiveDate} />
          <SourcesCard />
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
